import type {
  ChatHistoryMessageItem,
  ChatHistoryMessageMetadata,
  ChatMessage,
  ChatPanelTab,
  SelectedElement,
} from './types';
import { create } from 'zustand';

export type InterruptState = 'idle' | 'interrupting' | 'interrupted';

interface ChatState {
  messages: ChatMessage[];
  currentResponse: string;
  currentMessageId: string | null;
  optimisticPendingResponseMessageId: string | null;
  isSeedMessageWaitingResponse: boolean;
  isEmpty: boolean;
  isStreaming: boolean; // 对话流式输出是否正在进行中
  lastOutputDoneMessageId: string | null;
  isMessageLoading: boolean; // 消息列表是否正在加载中
  interruptState: InterruptState; // 中断状态
  // 分页状态
  historyLimit: number;
  nextHistoryCursor: string | null;
  historyMetadataByMessageId: Record<string, ChatHistoryMessageMetadata>;
  hasMoreHistory: boolean;
  isLoadingHistory: boolean; // 加载更多历史消息
  errorCount: number; // 对话错误次数计数
  // 折叠状态
  expandedRoundIds: Set<string>;
  isChatPanelVisible: boolean;
  activeTab: ChatPanelTab;
  isEditMode: boolean;
  selectedElements: SelectedElement[];
  inputFileCount: number;

  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setCurrentResponse: (currentResponse: string) => void;
  setCurrentMessageId: (messageId: string | null) => void;
  setOptimisticPendingResponseMessageId: (messageId: string | null) => void;
  setIsSeedMessageWaitingResponse: (isWaiting: boolean) => void;
  setIsEmpty: (isEmpty: boolean) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setLastOutputDoneMessageId: (messageId: string | null) => void;
  setIsMessageLoading: (isLoading: boolean) => void;
  setInterruptState: (state: InterruptState) => void;
  setNextHistoryCursor: (cursor: string | null) => void;
  replaceHistoryMetadata: (items: ChatHistoryMessageItem[]) => void;
  mergeHistoryMetadata: (items: ChatHistoryMessageItem[]) => void;
  setVersionFavorite: (commitId: string, isFavorite: boolean) => void;
  setHasMoreHistory: (hasMore: boolean) => void;
  setIsLoadingHistory: (isLoading: boolean) => void;
  getCurrentRoundNumber: () => number;
  setErrorCount: (count: number) => void;
  collapseRound: (messageId: string) => void;
  toggleRoundExpanded: (messageId: string) => void;
  setIsChatPanelVisible: (visible: boolean) => void;
  setActiveTab: (tab: ChatPanelTab) => void;
  setIsEditMode: (isEditMode: boolean) => void;
  setSelectedElements: (elements: SelectedElement[]) => void;
  clearSelectedElements: () => void;
  setInputFileCount: (count: number) => void;
  resetHistory: () => void;
  resetChat: () => void;
}

function getInitialState() {
  return {
    messages: [],
    currentResponse: '',
    currentMessageId: null,
    optimisticPendingResponseMessageId: null,
    isSeedMessageWaitingResponse: false,
    isEmpty: true,
    isStreaming: false,
    lastOutputDoneMessageId: null,
    isMessageLoading: false,
    historyLimit: 20,
    nextHistoryCursor: null,
    historyMetadataByMessageId: {},
    hasMoreHistory: false,
    isLoadingHistory: false,
    interruptState: 'idle' as InterruptState,
    errorCount: 0,
    expandedRoundIds: new Set<string>(),
    isChatPanelVisible: true,
    activeTab: 'chats' as ChatPanelTab,
    isEditMode: false,
    selectedElements: [] as SelectedElement[],
    inputFileCount: 0,
  };
}

export const useChatStore = create<ChatState>((set, get) => ({
  ...getInitialState(),

  setMessages: messages =>
    set(state => ({
      messages: typeof messages === 'function' ? messages(state.messages) : messages,
    })),
  setCurrentResponse: currentResponse => set({ currentResponse }),
  setCurrentMessageId: currentMessageId => set({ currentMessageId }),
  setOptimisticPendingResponseMessageId: optimisticPendingResponseMessageId => set({ optimisticPendingResponseMessageId }),
  setIsSeedMessageWaitingResponse: isSeedMessageWaitingResponse => set({ isSeedMessageWaitingResponse }),
  setIsEmpty: isEmpty => set({ isEmpty }),
  setIsStreaming: isStreaming => set({ isStreaming }),
  setLastOutputDoneMessageId: lastOutputDoneMessageId => set({ lastOutputDoneMessageId }),
  setIsMessageLoading: isMessageLoading => set({ isMessageLoading }),
  setInterruptState: interruptState => set({ interruptState }),
  setNextHistoryCursor: nextHistoryCursor => set({ nextHistoryCursor }),
  replaceHistoryMetadata: items => set({
    historyMetadataByMessageId: Object.fromEntries(items.map(item => [item.messageId, {
      commitId: item.commitId,
      previewUrl: item.previewUrl ?? null,
      createdAt: item.createdAt,
      hasRollbackMessages: item.hasRollbackMessages,
      userInput: item.userInput,
      isFavorite: item.isFavorite === 1 ? 1 : 0,
    }])),
  }),
  mergeHistoryMetadata: items => set(state => ({
    historyMetadataByMessageId: {
      ...state.historyMetadataByMessageId,
      ...Object.fromEntries(items.map(item => [item.messageId, {
        commitId: item.commitId,
        previewUrl: item.previewUrl ?? null,
        createdAt: item.createdAt,
        hasRollbackMessages: item.hasRollbackMessages,
        userInput: item.userInput,
        isFavorite: item.isFavorite === 1 ? 1 : 0,
      }])),
    },
  })),
  setVersionFavorite: (commitId, isFavorite) => set(state => ({
    historyMetadataByMessageId: Object.fromEntries(
      Object.entries(state.historyMetadataByMessageId).map(([messageId, metadata]) => [
        messageId,
        metadata.commitId === commitId
          ? { ...metadata, isFavorite: isFavorite ? 1 : 0 }
          : metadata,
      ]),
    ),
  })),
  setHasMoreHistory: hasMoreHistory => set({ hasMoreHistory }),
  setIsLoadingHistory: isLoadingHistory => set({ isLoadingHistory }),
  setErrorCount: errorCount => set({ errorCount }),
  setIsChatPanelVisible: isChatPanelVisible => set({ isChatPanelVisible }),
  setActiveTab: activeTab => set({ activeTab }),
  setIsEditMode: isEditMode => set({ isEditMode }),
  setSelectedElements: selectedElements => set({ selectedElements }),
  clearSelectedElements: () => set({ selectedElements: [] }),
  setInputFileCount: inputFileCount => set({ inputFileCount }),
  resetHistory: () => set({
    messages: [],
    nextHistoryCursor: null,
    historyMetadataByMessageId: {},
    hasMoreHistory: false,
    isLoadingHistory: false,
    isMessageLoading: false,
    expandedRoundIds: new Set<string>(),
  }),

  collapseRound: (messageId: string) => {
    const expandedIds = new Set(get().expandedRoundIds);
    expandedIds.delete(messageId);
    set({ expandedRoundIds: expandedIds });
  },

  toggleRoundExpanded: (messageId: string) => {
    const expandedIds = new Set(get().expandedRoundIds);
    if (expandedIds.has(messageId)) {
      expandedIds.delete(messageId);
    } else {
      expandedIds.add(messageId);
    }
    set({ expandedRoundIds: expandedIds });
  },

  // 计算当前对话轮次
  getCurrentRoundNumber: () => {
    const messages = get().messages;
    const userMessageCount = messages.filter(msg => msg.role === 'user').length;
    return userMessageCount;
  },

  resetChat: () => set(getInitialState()),
}));

export * from './types';
